import { PublicClient, Hash, TransactionReceipt, Account, Address } from "viem";
import { Logger } from "./logger";

const SCALE = BigInt(10_000);

function multiplyWithGasMultiplier(value: bigint, mult: number): bigint {
  const gas_multiplier_bps = BigInt(Math.ceil(mult * Number(SCALE)));
  return (value * gas_multiplier_bps) / SCALE;
}

const isNonceTooLow = (e: Error): boolean =>
  /nonce (too )?low|already known/i.test(e.message);

async function calculateAvgBlockTimeMs(
  client: PublicClient,
  blocks_to_sample = 100
): Promise<number> {
  const latest_block_number = await client.getBlockNumber();
  const sample_block_number = latest_block_number - BigInt(blocks_to_sample);

  const [latest_block, sample_block] = await Promise.all([
    client.getBlock({ blockNumber: latest_block_number }),
    client.getBlock({ blockNumber: sample_block_number }),
  ]);

  const time_diff_ms =
    Number(latest_block.timestamp - sample_block.timestamp) * 1000;
  return Math.floor(time_diff_ms / blocks_to_sample);
}

// Synthetic receipt for txs we know succeeded but don't have receipt for
const NONCE_TOO_LOW_SENTINEL = Symbol("NONCE_TOO_LOW");

type TxResult =
  | { status: "success"; receipt: TransactionReceipt }
  | { status: "already_mined"; nonce: number }
  | { status: "error"; error: Error };

export async function createTxBundler({
  account,
  public_client,
  logger,
  gas_base_multiplier = 1.1,
  blocks_count_as_stuck = 30,
  safe_confirmations = 1,
}: {
  account: Account | Address;
  public_client: PublicClient;
  logger: Logger;
  gas_base_multiplier?: number;
  blocks_count_as_stuck?: number;
  safe_confirmations?: number;
}) {
  const MAX_GAS_MULTIPLIER = 5;
  const GAS_BUMP_FACTOR = 1.1;

  logger = logger.withTag("tx-bundler");

  let gas_multiplier = gas_base_multiplier;
  let highest_succeeded_nonce = -1;

  const base_gas_per_nonce_map = new Map<
    number,
    {
      base_tip: bigint;
      base_max_fee: bigint;
      multiplier: number;
    }
  >();

  function increaseGasMultiplier(): void {
    gas_multiplier = Math.min(gas_multiplier * GAS_BUMP_FACTOR, MAX_GAS_MULTIPLIER);
  }

  function clearOldNonceData(up_to_nonce: number): void {
    for (const nonce of base_gas_per_nonce_map.keys()) {
      if (nonce <= up_to_nonce) {
        base_gas_per_nonce_map.delete(nonce);
      }
    }
  }

  const sender_address = typeof account === 'string' ? account : account.address;
  const avg_block_time_ms = await calculateAvgBlockTimeMs(public_client);

  return {
    avg_block_time_ms,

    async send({
      items,
    }: {
      items: {
        sendTransaction: (params: {
          nonce: number;
          maxPriorityFeePerGas: bigint;
          maxFeePerGas: bigint;
        }) => Promise<Hash>;
        formatError?(error: unknown): Error;
      }[];
    }): Promise<{
      receipts: TransactionReceipt[];
      errors: Error[];
      gas_multiplier: number;
      highest_succeeded_nonce: number;
      results: TxResult[];
    }> {
      let nonce: number;
      let is_replacing_stuck = false;

      // Detect stuck transactions
      for (let iteration = 0; ; iteration++) {
        const start_ts = Date.now();
        const [pending_nonce, latest_nonce] = await Promise.all([
          public_client.getTransactionCount({
            address: sender_address,
            blockTag: "pending",
          }),
          public_client.getTransactionCount({
            address: sender_address,
            blockTag: "latest",
          }),
        ]);
        const req_duration_ms = Date.now() - start_ts;

        if (pending_nonce === latest_nonce) {
          // No pending txs, fresh start
          nonce = pending_nonce;
          break;
        }

        // Stuck txs detected after waiting long enough
        if (iteration >= blocks_count_as_stuck) {
          nonce = latest_nonce;
          is_replacing_stuck = true;
          increaseGasMultiplier();
          logger.log(
            `Detected stuck tx(s), increasing gas multiplier to ${gas_multiplier.toFixed(2)}x and retrying from nonce ${latest_nonce}`
          );
          break;
        }

        await new Promise((res) =>
          setTimeout(res, Math.max(0, avg_block_time_ms - req_duration_ms))
        );
      }

      const fee_data = await public_client.estimateFeesPerGas();
      const base_max_fee = fee_data.maxFeePerGas;
      const base_tip = fee_data.maxPriorityFeePerGas;

      if (base_max_fee == null || base_tip == null) {
        throw new Error("Cannot determine gas price");
      }

      function calculateGasPrices(
        base_max_fee: bigint,
        base_tip: bigint,
        mult: number
      ): { maxFeePerGas: bigint; maxPriorityFeePerGas: bigint } {
        return {
          maxFeePerGas: multiplyWithGasMultiplier(base_max_fee, mult),
          maxPriorityFeePerGas: multiplyWithGasMultiplier(base_tip, mult),
        };
      }

      const sent_to_rpc = new Set<number>();
      const batch_min_nonce = nonce;
      const batch_max_nonce = nonce + items.length - 1;

      const txs_promises = items.map(
        async (tx, i): Promise<TxResult> => {
          const tx_nonce = nonce + i;

          try {
            // Use stored gas data if retrying a stuck tx, otherwise use fresh data
            const stored = base_gas_per_nonce_map.get(tx_nonce);
            const effective_base_tip = stored?.base_tip ?? base_tip;
            const effective_base_max_fee = stored?.base_max_fee ?? base_max_fee;
            const effective_multiplier = stored
              ? Math.max(gas_multiplier, stored.multiplier)
              : gas_multiplier;

            const gas_prices = calculateGasPrices(
              effective_base_max_fee,
              effective_base_tip,
              effective_multiplier
            );

            const tx_hash = await tx.sendTransaction({
              nonce: tx_nonce,
              ...gas_prices,
            });

            // RPC accepted the tx - record gas data in case it gets stuck
            sent_to_rpc.add(tx_nonce);
            base_gas_per_nonce_map.set(tx_nonce, {
              base_tip: effective_base_tip,
              base_max_fee: effective_base_max_fee,
              multiplier: effective_multiplier,
            });

            const timeout_ms = avg_block_time_ms * blocks_count_as_stuck;
            const receipt = await public_client.waitForTransactionReceipt({
              hash: tx_hash,
              confirmations: safe_confirmations,
              timeout: timeout_ms,
            });

            // Tx mined successfully - clean up
            base_gas_per_nonce_map.delete(tx_nonce);
            highest_succeeded_nonce = Math.max(highest_succeeded_nonce, tx_nonce);

            if (receipt.status === "reverted") {
              throw new Error(`Transaction ${tx_hash} reverted`);
            }

            return { status: "success", receipt };
          } catch (error) {
            // Handle "already mined" case - not an error
            if (error instanceof Error && isNonceTooLow(error)) {
              logger.log(
                `Nonce too low for ${tx_nonce}, tx was already mined`
              );
              base_gas_per_nonce_map.delete(tx_nonce);
              highest_succeeded_nonce = Math.max(highest_succeeded_nonce, tx_nonce);
              return { status: "already_mined", nonce: tx_nonce };
            }

            const formatted_error =
              tx.formatError && error instanceof Error
                ? tx.formatError(error)
                : error instanceof Error
                  ? error
                  : new Error(String(error));

            return { status: "error", error: formatted_error };
          }
        }
      );

      const results = await Promise.all(txs_promises);

      // Only clear nonce data for nonces below this batch to avoid
      // clearing data for in-flight txs within the batch
      if (highest_succeeded_nonce >= batch_min_nonce) {
        clearOldNonceData(batch_min_nonce - 1);
      }

      // Extract receipts and errors for backward compatibility
      const receipts: TransactionReceipt[] = [];
      const errors: Error[] = [];

      for (const result of results) {
        if (result.status === "success") {
          receipts.push(result.receipt);
        } else if (result.status === "error") {
          errors.push(result.error);
        }
        // "already_mined" is a success, just without a receipt
      }

      if (errors.length > 0) {
        logger.log(
          `Encountered ${errors.length} errors out of ${items.length} txs: ${errors.map((e) => e.message).join("; ")}`
        );
      }

      // Reset gas multiplier if all txs sent to RPC succeeded (or were already mined)
      const all_accepted = results.every((r) => r.status !== "error" || sent_to_rpc.has(nonce));
      if (all_accepted && !is_replacing_stuck) {
        gas_multiplier = gas_base_multiplier;
      }

      return {
        receipts,
        errors,
        gas_multiplier,
        highest_succeeded_nonce,
        results,
      };
    },
  };
}
